import { useSuspenseQuery } from '@tanstack/react-query'
import axios from 'axios'
import { z } from 'zod'
import { useParams } from 'react-router'
import { CompetitionLogoSchema } from '../../../navigation/api'

const CompetitionSchema = z.object({
  competition_id: z.number(),
  name: z.string(),
  logo: CompetitionLogoSchema,
  starts_at: z.string(),
  ends_at: z.string(),
  last_updated: z.string().nullable(),
  platform_logo: z.record(z.string()).nullable().optional(),
  single_player_teams: z.boolean(),
})

export type Competition = z.infer<typeof CompetitionSchema>

async function getCompetition(competitionId: number): Promise<Competition> {
  const response = await axios.get(`/api/competitions/${competitionId}`)
  return CompetitionSchema.parse(response.data)
}

export function useCompetition(competitionId: number | null) {
  const params = useParams()

  // Fallback to URL params if competition_id is not in state
  const effectiveCompetitionId =
    competitionId ||
    (params.competitionId ? parseInt(params.competitionId, 10) : null)

  return useSuspenseQuery({
    queryKey: ['competition', effectiveCompetitionId],
    queryFn: () => {
      if (!effectiveCompetitionId) {
        throw new Error('Competition ID is required')
      }
      return getCompetition(effectiveCompetitionId)
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}
